import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useModalAccessibility } from "@/lib/useModalAccessibility";
import { Outlet, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useMessagingRealtime } from "@/lib/data";
import { useIdleTimeout } from "@/lib/auth/useIdleTimeout";
import { AssistantProvider } from "@/components/assistant/AssistantProvider";
import { AppSidebar } from "./AppSidebar";
import { AppHeader } from "./AppHeader";
import { AppFooter } from "./AppFooter";
import { ImpersonationBar } from "./ImpersonationBar";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";

/**
 * Layout principal de l'espace applicatif (post-login).
 *
 * - Desktop : sidebar fixe à gauche (16rem), header sticky, contenu scrollable.
 * - Mobile  : sidebar en drawer (déclenchée depuis le burger du header).
 */
export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement>(null);
  useModalAccessibility(mobileRef, mobileOpen, () => setMobileOpen(false));
  const location = useLocation();

  // Abonnement Realtime à la messagerie de l'utilisateur courant
  useMessagingRealtime();

  // Déconnexion automatique après inactivité (OBS-11)
  const idle = useIdleTimeout();

  // Ferme le drawer mobile à chaque navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <AssistantProvider>
      <Dialog
        open={idle.warning}
        onClose={idle.extend}
        title="Votre session va expirer"
        description="Vous êtes inactif depuis 28 minutes. La déconnexion aura lieu dans deux minutes. Vous pouvez prolonger la session autant de fois que nécessaire."
        footer={
          <>
            <Button variant="ghost" onClick={() => void idle.logout()}>
              Me déconnecter
            </Button>
            <Button onClick={idle.extend}>Prolonger ma session</Button>
          </>
        }
      >
        <p role="status">Prolongez la session pour conserver l'accès à votre travail en cours.</p>
      </Dialog>
      <div className="flex min-h-screen bg-bg-subtle">
        {/* Sidebar — desktop */}
        <div className="hidden lg:block">
          <AppSidebar className="sticky top-0 h-screen" />
        </div>

        {/* Sidebar — mobile drawer */}
        {mobileOpen &&
          createPortal(
            <div
              ref={mobileRef}
              tabIndex={-1}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation principale"
              className={cn(
                "fixed inset-0 z-40 lg:hidden",
                mobileOpen ? "pointer-events-auto" : "pointer-events-none",
              )}
              aria-hidden={!mobileOpen}
            >
              {/* Backdrop */}
              <div
                className={cn(
                  "absolute inset-0 bg-bg-inverse/60 transition-opacity",
                  mobileOpen ? "opacity-100" : "opacity-0",
                )}
                onClick={() => setMobileOpen(false)}
              />
              {/* Drawer — même largeur que la sidebar (w-64) pour que la croix de
            fermeture reste bien à l'intérieur du panneau, sans vide à droite. */}
              <div
                className={cn(
                  "absolute inset-y-0 left-0 w-64 max-w-[85vw] transform transition-transform duration-200",
                  mobileOpen ? "translate-x-0" : "-translate-x-full",
                )}
              >
                <div className="relative h-full">
                  <AppSidebar onNavigate={() => setMobileOpen(false)} />
                  <button
                    type="button"
                    onClick={() => setMobileOpen(false)}
                    className="focus-ring absolute right-3 top-3 grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-bg-subtle"
                    aria-label="Fermer le menu"
                  >
                    <X className="size-5" />
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Main content — `min-w-0` indispensable : sans lui, un enfant plus large
          que l'écran (tableau, barre d'onglets, grille) élargit la colonne
          flex et fait déborder toute la page horizontalement sur mobile. */}
        <div className="flex min-h-screen min-w-0 flex-1 flex-col">
          <ImpersonationBar />
          <AppHeader onOpenMobileMenu={() => setMobileOpen(true)} />
          <main id="main-content" tabIndex={-1} className="flex-1 px-4 py-8 lg:px-8">
            <Outlet />
          </main>
          <AppFooter />
        </div>
      </div>
    </AssistantProvider>
  );
}
